import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

const BillingSuccess = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // Get the session ID from URL parameters
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setError('Invalid session information');
        setIsLoading(false);
        return;
      }

      // ✅ Payment was successful – notify the user and stop
      setError('Payment received! Please allow a few minutes for your subscription status to be updated.');
      setIsLoading(false);
    };

    if (user) {
      verifyPayment();
    } else {
      setIsLoading(false);
      setError('Please sign in to view your subscription details.');
    }
  }, [sessionId, user]);

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-b, gray.900, black)"
      color="white"
      position="relative"
      overflow="hidden"
    >
      <AppHeader pageTitle="Payment Successful" />

      <Container maxW="container.md" py={16} position="relative" mt="72px">
        <VStack spacing={8} align="center" textAlign="center">
          {isLoading ? (
            <Box textAlign="center" py={10}>
              <Spinner size="xl" color="blue.400" thickness="4px" mb={4} />
              <Text>Verifying your payment...</Text>
            </Box>
          ) : error ? (
            <Alert
              status="error"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              borderRadius="lg"
              py={6}
              bg="green.900"
              borderWidth="1px"
              borderColor="red.700"
            >
              <AlertTitle mt={4} mb={1} fontSize="xl">
                Payment Received
              </AlertTitle>
              <AlertDescription maxWidth="sm">
                {error}
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <Box
                p={4}
                borderRadius="full"
                bg="green.500"
                color="white"
                mb={4}
              >
                <Box as="span" fontSize="5xl" role="img" aria-label="success">
                  ✓
                </Box>
              </Box>
              
              <Heading as="h1" size="xl" fontWeight="bold">
                Payment Successful!
              </Heading>
              
              <Text fontSize="lg" color="gray.400" maxW="md">
                Thank you for subscribing to SyncdIt Pro. Your payment has been processed successfully.
              </Text>
              
              {subscription && (
                <Box 
                  w="full" 
                  p={6} 
                  borderRadius="xl" 
                  bg="green.800" 
                  borderWidth="1px" 
                  borderColor="green.700"
                >
                  <VStack align="start" spacing={3} w="full">
                    <Heading size="md">Subscription Details</Heading>
                    <Box w="full" borderTopWidth="1px" borderColor="green.700" pt={3}>
                      <Text><strong>Plan:</strong> {subscription.plan_name}</Text>
                      <Text><strong>Status:</strong> {subscription.status}</Text>
                      <Text>
                        <strong>Next billing date:</strong> {new Date(subscription.next_bill_date).toLocaleDateString()}
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              )}
              
              <VStack spacing={4} w="full" pt={6}>
                <Button
                  colorScheme="blue"
                  size="lg"
                  width="full"
                  onClick={() => navigate('/player')}
                >
                  Go to Player
                </Button>
                <Button
                  variant="outline"
                  size="md"
                  width="full"
                  onClick={() => navigate('/settings')}
                >
                  Manage Subscription
                </Button>
              </VStack>
            </>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default BillingSuccess; 